import React, {useEffect, useState} from "react";
import {SupportSlaDto, SupportSlaRestControllerApiFactory} from "arteco-api-client-ts";
import {
	Alert, Link, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from "@mui/material";
import {useApiManager} from "../../../../components/utils/api";
import {EmptyState, Page, PageHeader, Panel, SkeletonRows, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";

const REDMINE = "https://redmine.artecoapps.com/issues/";

const fecha = (iso?: string) => iso
	? new Date(iso).toLocaleString("es-ES", {timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"})
	: "-";

const plazo = (h?: number | null) => h == null ? "sin compromiso" : `${h} h laborables`;

/**
 * Plazo de respuesta del soporte por cliente con plan (#13714): cada solicitud del mes, cuándo
 * respondió Arteco por primera vez y si entró en plazo. Horario L-J 8-17, V 8-14, festivos de Palma.
 */
export const SupportSlaPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(SupportSlaRestControllerApiFactory);
	const hoy = new Date();
	const [mes, setMes] = useState<string>(`${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`);
	const [datos, setDatos] = useState<SupportSlaDto[]>();

	useEffect(() => {
		setDatos(undefined);
		const [y, m] = mes.split("-").map(Number);
		apiManager.execute(ctrl.report(y, m)).then(setDatos);
	}, [mes]);

	const meses = Array.from({length: 12}, (_, i) => {
		const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
	});

	const monthLabel = (m: string) => {
		const [y, mm] = m.split("-").map(Number);
		const t = new Date(y, mm - 1, 1).toLocaleDateString("es-ES", {month: "long", year: "numeric"});
		return t.charAt(0).toUpperCase() + t.substring(1);
	};

	return (
		<Page>
			<PageHeader title={"Plazos de respuesta del soporte"}
						subtitle={"Solicitudes abiertas por el cliente en su proyecto de soporte. Primera respuesta: la primera nota pública o cambio de estado o asignación de Arteco. Horario L-J 8-17 y V 8-14, sin festivos de Palma."}
						actions={<TextField select size="small" label="Mes" value={mes} onChange={(e) => setMes(e.target.value)} sx={{minWidth: 180}}>
							{meses.map(m => <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>)}
						</TextField>}/>

			{!datos && <SkeletonRows rows={4}/>}
			{datos && datos.length === 0 && (
				<Panel>
					<EmptyState title="Ninguna cuota activa tiene proyecto de soporte"
								description="Se configura en la ficha del presupuesto (tipo cuota)."/>
				</Panel>
			)}

			<Stack spacing={3}>
				{(datos || []).map(c => (
					<Panel key={c.budgetId} title={`${c.customer} · ${c.plan}`}
						   subtitle={`Proyecto ${c.supportProject} · normal: ${plazo(c.normalHours)} · crítico: ${plazo(c.criticalHours ?? c.normalHours)}`}>
						{c.error && <Alert severity="error" sx={{mb: 2}}>{c.error}</Alert>}
						{!c.error && (
							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 2}}>
								<SoftChip tone="neutral" label={`${(c.requests || []).length} solicitudes`}/>
								<SoftChip tone="success" label={`${c.met} en plazo`}/>
								<SoftChip tone={c.breached ? "error" : "neutral"} label={`${c.breached} fuera de plazo`}/>
								<SoftChip tone={c.pending ? "warning" : "neutral"} label={`${c.pending} sin respuesta`}/>
							</Stack>
						)}
						<TableContainer sx={tableContainerSx}>
							<Table size="small" sx={{...tableSx, tableLayout: "auto", minWidth: 760}}>
								<TableHead>
									<TableRow>
										<TableCell>#</TableCell><TableCell>Asunto</TableCell><TableCell>Prioridad</TableCell>
										<TableCell>Entrada</TableCell><TableCell>1.ª respuesta</TableCell>
										<TableCell align="right">Horas</TableCell><TableCell align="right">Plazo</TableCell>
										<TableCell>Resultado</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{(c.requests || []).map(r => (
										<TableRow key={r.issueId}>
											<TableCell><Link href={REDMINE + r.issueId} target="_blank" rel="noreferrer" underline="hover">{r.issueId}</Link></TableCell>
											<TableCell>{r.subject}</TableCell>
											<TableCell>{r.priority}{r.critical ? " (crítica)" : ""}</TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}>{fecha(r.createdOn)}</TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}>{r.pending ? "sin respuesta" : fecha(r.firstResponse)}</TableCell>
											<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{r.hours}</TableCell>
											<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{r.limit ?? "-"}</TableCell>
											<TableCell>
												<SoftChip tone={r.met === false ? "error" : r.met ? "success" : r.pending ? "warning" : "neutral"}
														  label={r.met == null ? (r.pending ? "Pendiente" : "Sin compromiso") : r.met ? "En plazo" : "Fuera de plazo"}/>
											</TableCell>
										</TableRow>
									))}
									{(c.requests || []).length === 0 && !c.error && (
										<TableRow><TableCell colSpan={8} sx={{color: "text.secondary"}}>Sin solicitudes del cliente este mes.</TableCell></TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Panel>
				))}
			</Stack>
		</Page>
	);
};
